import { z } from 'zod';

import { resolveUnitToBaseRate } from '#/modules/imsMgmt/helper/materialUnit/resolveUnitToBaseRate';
import { STOCK_ISSUE_LINE_SOURCE_TYPE } from '#/modules/imsMgmt/receipts/stockIssue/helper/issueSourceType';
import { maxIssueQtyInUnit } from '#/modules/imsMgmt/receipts/stockIssue/helper/issueQtyBounds';

function isPositiveInteger(value) {
  if (value === '' || value == null) {
    return false;
  }
  const n = Number(value);
  return Number.isFinite(n) && Number.isInteger(n) && n >= 1;
}

const lineSchema = z.object({
  sourceType: z.literal(STOCK_ISSUE_LINE_SOURCE_TYPE).default(STOCK_ISSUE_LINE_SOURCE_TYPE),
  invStockId: z.string().min(1, '請選擇庫存'),
  stockNo: z.string().optional().nullable(),
  mtrId: z.string().optional().nullable(),
  mtrNo: z.string().optional().nullable(),
  mtrName: z.string().optional().nullable(),
  warehouseLabel: z.string().optional().nullable(),
  /** 基本單位可用量（非所選 unit 語意） */
  availableQty: z.union([z.string(), z.number()]).optional().nullable(),
  /** 可選；空則後端用物料 base */
  unitId: z.string().optional().nullable(),
  unitOptions: z.array(z.any()).optional().nullable(),
  qty: z.union([z.string(), z.number(), z.null()]),
  remark: z.string().optional().nullable(),
}).superRefine((line, ctx) => {
  const qty = line.qty;
  if (!isPositiveInteger(qty)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '出庫數量須為 ≥ 1 的整數',
      path: ['qty'],
    });
    return;
  }

  const n = Number(qty);
  const available = Number(line.availableQty);
  if (!Number.isFinite(available) || available <= 0) {
    return;
  }

  const maxQty = maxIssueQtyInUnit(available, line.unitOptions, line.unitId);
  if (n > maxQty) {
    const rate = resolveUnitToBaseRate(line.unitOptions, line.unitId) ?? 1;
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: rate === 1
        ? `出庫數量不可超過可用量（基本單位 ${available}）`
        : `出庫數量不可超過本單位上限（${maxQty}；基本單位可用 ${available}）`,
      path: ['qty'],
    });
  }
});

export const stockIssueFormSchema = z.object({
  issueDate: z.string().min(1, '請填寫出庫日'),
  stockIssueCategoryId: z.string().min(1, '請選擇出庫類別'),
  issueReason: z.string().optional().nullable(),
  remark: z.string().optional().nullable(),
  lines: z.array(lineSchema).min(1, '至少一筆明細').superRefine((lines, ctx) => {
    const seen = new Set();
    lines.forEach((line, index) => {
      const id = line.invStockId?.trim();
      if (!id) {
        return;
      }
      if (seen.has(id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '同一庫存不可重複',
          path: [index, 'invStockId'],
        });
        return;
      }
      seen.add(id);
    });
  }),
});

export const STOCK_ISSUE_FIELD_LABELS = {
  issueDate: '出庫日',
  stockIssueCategoryId: '出庫類別',
  issueReason: '出庫事由',
  remark: '備註',
  lines: '明細',
  'lines.invStockId': '庫存',
  'lines.unitId': '單位',
  'lines.qty': '出庫數量',
  'lines.remark': '行備註',
};

export const ISSUE_LINES_TIP = '多選庫存後確認；同單不可重複同一庫存。數量為所選單位；可用量以基本單位計。';
